import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { Utilities } from '../class/utilities';
import { CashTitle, Receive, TitlePatchFields } from '../interface/cash-title';
import type { TitleRenegotiationResponse } from '../interface/title-renegotiation-response';
import { Invoice } from '../interface/invoice';
import { NFe } from '../interface/NF-e';
import { NFSe, RPS } from '../interface/NFS-e';
import { Finance } from '../interface/ro-finance';
import { TitleGroupOptionsResponse } from '../interface/os-group';
import { environment } from '../../environments/environment';
import { ApiPaginatedTitleQueryParams, ApiPaginatedTitlesResponse, PaginationService } from './pagination.service';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DataService } from './data.service';
import { API_ERRORS } from '../shared/lists/errors';
import { ObjectId } from '../shared/type-aliases/object-id';
import { IsoString } from '../shared/type-aliases/iso-string';
import { Address } from '../interface/address';
import { Company } from '../interface/company';
import { Supplier } from '../interface/supplier';
import moment, { Moment } from 'moment';
import { MkgOS } from '../class/mkg-os';
import { CashTitleHandlerService } from './cash-title-handler.service';
import { SessionError } from './session/mkg-session';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LayoutService } from './layout.service';
import { SessionTitleService } from './session/session-title.service';
import { PaymentCondition } from '../interface/paymentCondition';
import { Operation } from '../interface/operation';
import { LOG_REPORTER_KEYS, LogReporterService } from './log-reporter.service';
import { BOOLEAN_NUMBER } from '../shared/common-enums/boolean-number';
import { CASH_TITLE_MOVEMENT_TYPE, CreateCashTitleMovimentationParamethers } from '../interface/cash-log-interfaces';
import { HolidayService } from '@app/component/page/company/records/holidays/services/holiday.service';
import { NF_response } from '@app/interface/NF-response';
import { ApplyLowOptions } from '@app/component/page/company/financial/apply-low/confirm-apply-low/confirm-apply-low.component';
import { IStatusTitleApiIn } from '@app/component/page/company/records/status-title/models/status-title.model';
import { OPERATION_ID_HEADER } from './app-request-headers.service';
import { OperationIdService } from './operation-id.service';
import type { ICancelLowOptions } from '../interface/cancel-low-options';
import type { ApplyLowRequestOptions } from '../interface/apply-low-request-options';

export interface TitlesTotal {
  balancePayTotal: number;
  balanceReceiveTotal: number;
  valuePayTotal: number;
  valueReceiveTotal: number;
  paidTotal: number;
  receivedTotal: number;
  /** Company id */
  _id?: ObjectId;
  /**
   * Aggregates by situation, returned only when the request opts-in via `summary=1`.
   *
   * Calculated over the filter cut of the query **WITHOUT the `situation` filter itself** — the
   * same technique used by `typeCounts` for the tab's `type` filter — so selecting a situation
   * never zeroes the other buckets and the KPI cards stay usable as navigation.
   *
   * `balance` carries the open balance for `overdue`/`dueToday`/`upcoming`, and the title **value**
   * for `settled` (whose balance is zero by definition).
   */
  situation?: {
    overdue: { count: number; balance: number };
    dueToday: { count: number; balance: number };
    upcoming: { count: number; balance: number };
    settled: { count: number; balance: number };
    /**
     * Portion of the settled titles closed by renegotiation (not money received). Optional: absent
     * against an older back-end. `count` includes partially renegotiated titles, also in `settled`.
     */
    renegotiated?: { count: number; balance: number };
  };
  /**
   * Counts by title type (pay/receive), returned only when the request opts-in via `summary=1`.
   * Calculated over the cut WITHOUT the active tab's type filter, so inactive tabs still have a count.
   */
  typeCounts?: {
    pay: number;
    receive: number;
  };
}

export interface ApiCashTitleIn {
  _id?: string;
  company?: string;
  idOS?: string;
  groupId?: string;
  orderId?: string;
  companyOrder?: string;
  companyCode?: string;
  type?: string;
  supplier_client?: string;
  clientTmp?: string;
  expenseType?: string;
  /** Rateio opcional da despesa (apenas título a pagar); ausente/vazio ⇒ usa `expenseType` único. */
  expenseAllocations?: { expenseType: string; value: number }[];
  paymentCondition?: string;
  invoiceNumber?: string;
  serial?: string;
  /** Id of the invoice (NF/NFSe) that stamped this title (Phase B). */
  invoiceId?: string;
  movementDate?: string;
  expirationDate?: string;
  observation?: string;
  value?: number;
  parcel?: number;
  balance?: number;
  status?: 0 | 1 | 2;
  bank?: CashTitle['bank'];
  received?: Receive[];
  adiantamento?: 0 | 1;
  provisional?: BOOLEAN_NUMBER;
  rateTitle?: number;
  statusTitle?: IStatusTitleApiIn;
  boleto?: CashTitle['boleto'];
  currentInstallment?: number;
  totalInstallments?: number;
}

export interface PaginatedTitle {
  readonly available?: 0 | 1;
  readonly _id?: ObjectId;
  readonly company?: ObjectId;
  readonly createdAt: IsoString;
  readonly updatedAt: IsoString;
  readonly seq: number;
  readonly __v: number;

  bank?: {
    code: string;
    name: string;
    operation: string;
    account: string;
  }
  companyCode?: string;
  idOS?: string;
  groupId?: string;
  /** Sequential code of the grouping (`os-groups.code`), resolved by the API per page. */
  groupCode?: number;
  /** Issuing company of the grouping; only present when it is not the logged company. */
  groupCompany?: { name: string; cnpj: string };
  companyOrder?: string;
  orderId?: string;
  /** Id of the invoice (NF/NFSe) that stamped this title (Phase B). Empty on pre-deploy titles. */
  invoiceId?: string;
  type?: ObjectId;
  titleTypePopulate: {
    description: string;
    /** 
     * true = pay
     * false = receive
     */
    payReceive: boolean;
  }

  clientTmp?: string;

  qtdaPortion?: number;
  flag?: string;

  readonly supplier_client: {
    _id: ObjectId,
    name: string;
    document: string;
    vehicles: Array<string>;
    available: 0 | 1;
    gender: number;
    lang: number;
    maritalStatus: number;
    blocked: boolean;
    subscribed: boolean;
    kind: any;
    updateHistory: Array<any>;
    createdAt: IsoString;
    updatedAt: IsoString;
    rg: string;
    phone1: string;
    phone2: string;
    address: Address;
    lastUpdate: IsoString;
    type: any;
    documentType: number;
    hash: string,
    marketing: number,
    __v: number,
    fancyName?: string,
    birthday?: IsoString,
    taxpayer?: boolean,
    retCOFINS?: boolean,
    retCSLL?: boolean,
    retINSS?: boolean,
    retIRPJ?: boolean,
    retPIS?: boolean,
    lead?: any,
    email?: string,
    creditLimit?: number,
    cardAdmin?: Supplier['cardAdmin']
  },

  invoiceNumber?: string;
  serial?: string;
  paymentCondition: ObjectId;
  expenseType?: ObjectId;
  value: number;
  parcel: number;
  observation: string;
  balance: number;
  provisional?: BOOLEAN_NUMBER;
  paymentConditionPopulate?: PaymentCondition;

  /**
 * When the Cash Titles are created from OS or Order, the status is inactive (0).
 * So, when OS is closed, all titles are updated to active (status = 1).
 * Whem a order has its status changed from "ORÇAMENTO"(1) to "PEDIDO"(2), his titles also
 * must be updated to active status.
 *
 * If the title is created from invoice, the status is active directly (1).
 *
 * Whan the title cannot be deleted (already have received lows), the status is seetted to canceled(2)
 *
 * - 0 - inactive
 * - 1 - active
 * - 2 - canceled
 * */
  status: 0 | 1 | 2;
  received: Receive[];
  /** no get por id isso é boolean, no get paginado é number? */
  adiantamento: number;
  expirationDate?: IsoString;
  movementDate: IsoString;
  rateTitle?: number;

  /** Local only */
  cnpj: string;
  fees?: number;
  penaltyFee?: number;
  rate?: number;
  discount?: number;
  /** local only */
  toPay?: number;
  statusTitle?: IStatusTitleApiIn;
  boleto?: {
    _id?: string;
    boleto_link?: string;
    group_link?: string;
    status?: string;
  };

  currentInstallment?: number;
  totalInstallments?: number;

  /**
   * Due situation of the title, computed by the back-end and returned only when the request
   * opts-in via `summary=1`. The classification rule lives solely in the back-end.
   */
  situation?: 'overdue' | 'dueToday' | 'upcoming' | 'settled' | 'renegotiated';

  /**
   * Settled value of this title — the sum of `totalValue + fees + penaltyFee − rate − discount` of
   * its **direct, active, non-renegotiation** lows. Computed by the back-end and returned only
   * when the request opts-in via `summary=1`; **optional on purpose**, so the screen degrades to an
   * empty cell against an older back-end instead of showing a wrong number.
   *
   * Lows linked to Card Operator titles (`received[].refId` non-empty) are excluded: that money is
   * counted once, on the operator title's own low. A service-order title fully settled by card
   * therefore reports **zero** here, while its baixa history footer still shows the low's value —
   * the history sums every active low, including the linked one. The divergence is by construction.
   *
   * Never recompute it from `received[]` in the front: the totals in the footer come from the
   * back-end, and it was a second implementation of this very rule that produced the duplicated
   * total this field exists to fix.
   */
  settledValue?: number;
}


@Injectable({
  providedIn: 'root'
})
export class CashTitleService {

  private now = new Date();
  cashTitles: CashTitle[];
  private _logReporterService = inject(LogReporterService);
  private _operationIds = inject(OperationIdService);

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private _dataService: DataService,
    private _cashTitleHandlerService: CashTitleHandlerService,
    private _sessionTitleService: SessionTitleService,
    private _dialog: MatDialog,
    private _layout: LayoutService,
    private _holidayService: HolidayService
  ) { }

  /**
   *  @deprecated Try to use `getPaginated()`, `findOStitles()` or `findOrderTitles()` instead
   */
  async getAll(cnpj: string, skipInactives = true): Promise<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    await this._cashTitleHandlerService.getData();
    const resp = await firstValueFrom(this.http.get<{ titles: any[] }>(url, header).pipe(
      first(),
      tap((resp) => {
        const wrongTitles = [];
        for (const title of resp.titles) {
          const ids = new Set(title.received.reduce((arr, received) => arr.concat(received._id), []));
          if (ids.size !== title.received.length) {
            wrongTitles.push(title)
          }
        }
        if (wrongTitles.length) {
          console.warn('Alguns títulos possuem baixas com o mesmo id', wrongTitles)
        }
      }),
    ));
    let titles: CashTitle[] = [];
    for (let title of resp.titles) {
      const titleApp = await this._cashTitleHandlerService.complyAPP(title);
      titles.unshift(titleApp)
    }
    this.cashTitles = skipInactives ? titles.filter(t => t.status !== 0) : titles;
    return titles;
  }

  /**
   * Options for the "Agrupamento" autocomplete: the groupings with the typed code that have titles in
   * the logged company, grouped by the company that issued them.
   *
   * @param code what the user typed; "6" and "Agrupamento 6" are equivalent
   */
  getGroupOptions(cnpj: string, code: string): Observable<TitleGroupOptionsResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/group-options`;
    const params = new HttpParams().set('code', code);
    return this.dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<TitleGroupOptionsResponse>(url, options)),
      first()
    );
  }

  getTitlesBeforeExpirationDateStart(cnpj: string, expirationDate: Moment): Observable<PaginatedTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/expiration-date`;
    const params = new HttpParams().append('expirationDate', expirationDate.toISOString());
    return this.dataService.httpOptions(cnpj, params)
      .pipe(switchMap(header => this.http.get<PaginatedTitle[]>(url, header)));
  }

  /**
   * Attention: this method DON'T return inactive titles
   *
   * if you want to find inactive titles of an OS, use `findOStitles()` instead
   *
   * if you want to find inactive titles of an Order, use `findOrderTitles()` instead
   *
   * @param matrix If the company have matrix, pass it to get name of supplier from updateHistory
   */
  getPaginated(cnpj: string, apiParams: ApiPaginatedTitleQueryParams, matrix?: Company): Observable<ApiPaginatedTitlesResponse<PaginatedTitle>> {
    const url = `${environment.mkgoURL}/api/v1/title/pages`;
    let params = PaginationService.getParams(apiParams);
    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<ApiPaginatedTitlesResponse<PaginatedTitle>>(url, options)),
      first(),
      switchMap(resp => {
        // the API return 100 docs by defult (even if all=1), so if we want all docs we must call it again with param `limit`
        if (apiParams.all && resp.hasNextPage) {
          apiParams.limit = resp.totalDocs;
          params = PaginationService.getParams(apiParams);
          return this._dataService.httpOptions(cnpj, params).pipe(
            switchMap(options2 => this.http.get<ApiPaginatedTitlesResponse<PaginatedTitle>>(url, options2)),
            first()
          )
        } else {
          return of(resp)
        }
      }),
      map(resp => {
        if (!resp.totals) {
          resp.totals = {
            _id: "",
            paidTotal: 0,
            receivedTotal: 0,
            valuePayTotal: 0,
            valueReceiveTotal: 0,
            balancePayTotal: 0,
            balanceReceiveTotal: 0,
          };
        }

        /** @TODO pass it to backend */
        resp.docs.map(title => {
          if (matrix && title.supplier_client['updateHistory']) {
            const clientHistory = title.supplier_client['updateHistory'].find(hist => hist.company === matrix.id);
            if (clientHistory) {
              title.supplier_client.name = clientHistory.name;
            }
          }
          title.cnpj = cnpj;
          return title
        });
        return resp;
      }),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of<ApiPaginatedTitlesResponse<PaginatedTitle>>({
            docs: <PaginatedTitle[]>[],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: null,
            page: 0,
            pagingCounter: 0,
            prevPage: null,
            totalDocs: 0,
            totalPages: 0,
            totals: {
              balancePayTotal: 0,
              balanceReceiveTotal: 0,
              paidTotal: 0,
              receivedTotal: 0,
              valuePayTotal: 0,
              valueReceiveTotal: 0,
              _id: ""
            }
          })
        } else {
          throw err;
        }
      }),
      map(resp => {
        resp.docs.map(t => {
          t.cnpj = cnpj || this.dataService.company.cnpj;
          const received = t.received;
          t.fees = received.reduce((sum, low) => sum + (low.fees || 0), 0);
          t.penaltyFee = received.reduce((sum, low) => sum + (low.penaltyFee || 0), 0);
          t.rate = received.reduce((sum, low) => sum + (low.rate || 0), 0);
          t.discount = received.reduce((sum, low) => sum + (low.discount || 0), 0);
          t.rateTitle = t.rateTitle ?? 0
        });
        return resp
      })
    );
  }

  async getById(id: string): Promise<CashTitle> {
    const url = `${environment.mkgoURL}/api/v1/title/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(false));
    const resp = await firstValueFrom(this.http.get<any>(url, header)
      .pipe(
        catchError(async error => {

          if (error.status === 400 && (error.error as SessionError).userId) {
            const sessionError = error.error as SessionError;
            this._layout.loader = false;
            const result = await firstValueFrom(this._dialog.open(ConfirmationDialogComponent, {
              data: {
                text: "TITLE_IN_USE",
                param: sessionError,
                uniqueAction: false
              }
            }).afterClosed());
            if (result) {
              const os = await firstValueFrom(this._sessionTitleService.closeSession(id, sessionError._id));
              return os;
            }
          }
          throw error;
        })
      ));
    await this._cashTitleHandlerService.getData();
    const title = this._cashTitleHandlerService.complyAPP(resp);
    return title;
  }

  /**
   * @param personId ID of Client or Supplier
   * @returns an array of advance-titles
   */
  async getAdvancesOfPerson(personId: ObjectId): Promise<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/client/${personId}`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    return firstValueFrom(this.http.get(url, options).pipe(
      first(),
      map((arr: any[]) => arr.map(obj => this._cashTitleHandlerService.complyAPP(obj)))
    ));
  }

  async create(title: CashTitle, cnpj: string, options: Partial<CreateCashTitleMovimentationParamethers>): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/title`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const body = JSON.stringify({
      ...this._cashTitleHandlerService.complyAPI(title),
      ...(options.reason ? { reason: options.reason } : {}),
      ...(options.origin !== undefined ? { createdBy: options.origin } : {})
    });

    try {
      const resp = await firstValueFrom(this.http.post<{ id: ObjectId }>(url, body, header));
      title._id = resp.id;
      return resp.id;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async cancelBoletoByTitle(titleId: string): Promise<void> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/cancel-boleto`;
    const header = await firstValueFrom(this.dataService.httpOptions(false));
    await firstValueFrom(this.http.post<void>(url, {}, header));
  }

  async cancelTitle(id, reason, substituteTitleId?, cnpj?) {
    const url = `${environment.mkgoURL}/api/v1/title/${id}/cancel`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(companyId)),
      this._operationIds.begin()
    );
    const body = {
      reason,
      ...(substituteTitleId ? { substituteTitleId } : {})
    };

    return firstValueFrom(this.http.post(url, body, header));
  }

  async activateTitle(id, reason, stamps, cnpj = this.dataService.company.cnpj) {
    const url = `${environment.mkgoURL}/api/v1/title/${id}/activate`;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(cnpj || this.dataService.company.cnpj)),
      this._operationIds.begin()
    );

    return firstValueFrom(this.http.post(url, { ...stamps, reason }, header));
  }

  async useAdvance(titleId, body, operationId) {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/use-advance`;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(this.dataService.company.cnpj)),
      operationId
    );

    return firstValueFrom(this.http.post(url, body, header));
  }

  async renegotiate(body, operationId) {
    const url = `${environment.mkgoURL}/api/v1/title/renegotiate`;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(this.dataService.company.cnpj)),
      operationId
    );

    return firstValueFrom(this.http.post<TitleRenegotiationResponse>(url, body, header));
  }

  async createAdvance(body, operationId) {
    const url = `${environment.mkgoURL}/api/v1/title/advance`;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(this.dataService.company.cnpj)),
      operationId
    );

    return firstValueFrom(this.http.post(url, body, header));
  }

  async filter(
    args: {
      range?: {
        startDate?: string,
        endDate?: string
      }
      forceReceived?: boolean,
      totalByCondition?: boolean,
      balanceOtherThan?: number
    }
  ) {
    let url = `${environment.mkgoURL}/api/v1/title/filter`;
    let params = new HttpParams();

    if (args.range) {
      if (args.range.startDate) {
        params = params.append('startDate', args.range.startDate)
      }
      if (args.range.endDate) {
        params = params.append('endDate', args.range.endDate)
      }
    }

    if (args.hasOwnProperty('forceReceived')) {
      params = params.append('forceReceived', `${args.forceReceived}`)
    }
    if (args.hasOwnProperty('totalByCondition')) {
      params = params.append('totalByCondition', `${args.totalByCondition}`)
    }
    if (Number.isFinite(args.balanceOtherThan)) {
      params = params.append('balanceOtherThan', `${args.balanceOtherThan}`)
    }

    const header = await firstValueFrom(this.dataService.httpOptions(false));
    header['params'] = params;
    return firstValueFrom(this.http.get<{
      received: Receive,
      supplier_client: ObjectId,
      type: ObjectId
    }[]
    >(url, header).pipe(
      map((titles) => titles.map((t: any) => {
        t.received = [t.received];
        return this._cashTitleHandlerService.complyAPP(t)
      }))
    ));
  }

  public async delete(id: string, cnpj: string, options: Partial<CreateCashTitleMovimentationParamethers>) {
    const url = `${environment.mkgoURL}/api/v1/title/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const deleteUrl = options.reason
      ? `${url}?reason=${encodeURIComponent(options.reason)}`
      : url;

    try {
      return await firstValueFrom(this.http.delete(deleteUrl, header));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * @returns The titles of OS (including inactives)
   *
   * `companyCode` (OS.codeSystem) is a per-company sequence, so the API's fallback branch can
   * return titles of a same-numbered OS from another branch (unified financial / matrix-branch
   * CNPJ placement). Titles that carry an `idOS` are re-checked here; `idOS`-less legacy titles
   * are kept on the `companyCode` match so old records stay visible (see OS-TITLES-SCOPE-FINDINGS.md).
   */
  public findOsTitles(idOS: string, codeSystem: string | number, cnpj: string): Observable<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/osv2`;
    const params = PaginationService.getParams({
      idOS,
      companyCode: codeSystem
    });

    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<{ titles: ApiCashTitleIn[] }>(url, options)),
      first(),
      switchMap(r => from(this._cashTitleHandlerService.getData().then(() => {
        const owned = r.titles.filter(title => !title.idOS || String(title.idOS) === String(idOS));

        // Observability for the collision fix: a non-zero count here means the API is still
        // returning foreign-OS titles (fix not deployed, or a new regression).
        const foreign = r.titles.length - owned.length;
        if (foreign) {
          const foreignIds = r.titles.filter(t => t.idOS && String(t.idOS) !== String(idOS)).map(t => t._id);
          this._logReporterService.errorLogger(
            `CashTitleService.findOsTitles(${idOS}, ${codeSystem})`, LOG_REPORTER_KEYS.INVOICE
          ).call(this, `descartados ${foreign} título(s) de outra OS: ${JSON.stringify(foreignIds)}`);
        }

        return owned.map(title => this._cashTitleHandlerService.complyAPP(title));
      }))),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of([] as Array<CashTitle>)
        } else {
          this._logReporterService.errorLogger(`CashtitleService.findOsTitles(""${idOS}, ${codeSystem})`, LOG_REPORTER_KEYS.INVOICE).call(this, err);
          throw err;
        }
      })
    )
  }

  /**
   * @returns The titles of Order (including inactives)
   *
   * Same `companyOrder` collision as `findOsTitles` — `Order.code` is likewise a per-company
   * sequence, so titles carrying an `orderId` are re-checked here.
   */
  public findOrderTitles(orderId: string, companyOrder: string | number, cnpj: string): Observable<CashTitle[]> {
    const url = `${environment.mkgoURL}/api/v1/title/osv3`;
    const params = PaginationService.getParams({
      orderId,
      companyOrder
    });

    return this._dataService.httpOptions(cnpj, params).pipe(
      switchMap(options => this.http.get<{ titles: ApiCashTitleIn[] }>(url, options)),
      first(),
      switchMap(r => from(this._cashTitleHandlerService.getData().then(() => {
        const owned = r.titles.filter(title => !title.orderId || String(title.orderId) === String(orderId));

        const foreign = r.titles.length - owned.length;
        if (foreign) {
          const foreignIds = r.titles.filter(t => t.orderId && String(t.orderId) !== String(orderId)).map(t => t._id);
          this._logReporterService.errorLogger(
            `CashTitleService.findOrderTitles(${orderId}, ${companyOrder})`, LOG_REPORTER_KEYS.INVOICE
          ).call(this, `descartados ${foreign} título(s) de outro Order: ${JSON.stringify(foreignIds)}`);
        }

        return owned.map(title => this._cashTitleHandlerService.complyAPP(title));
      }))),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of([] as Array<CashTitle>)
        } else {
          throw err;
        }
      })
    )
  }

  getTotals() {
    const url = `${environment.mkgoURL}/api/v1/title/totals`;
    return this._dataService.httpOptions(false).pipe(
      switchMap((options) => this.http.get<TitlesTotal>(url, options)))
  }

  public getExpirationDate(days: number, from: Date = new Date()): Observable<Date> {
    const milisseconds = days * Utilities.MS_DAY;
    let due = new Date(from.getTime() + milisseconds)

    return this._holidayService.getNextBusinessDay(due).pipe(
      map(resp => {
        if (resp.nextBusinessDay) {
          console.log('Ajustando data de vencimento para o próximo dia útil:', resp.nextBusinessDay);
          return new Date(resp.nextBusinessDay);
        }
        return due;
      }),
      catchError(() => of(due))
    );
  }


  /** fix rounding difference */
  public fixDifference(titles: CashTitle[], invoiceValue: number): CashTitle[] {
    if (!titles.length) {
      return []
    }
    let sum = 0;
    for (const title of titles) {
      sum += title.value;
    }
    const difference = Number((invoiceValue - sum).toFixed(2));
    titles[0].value = Number(((titles[0].value || 0) + difference).toFixed(2));
    if (titles[0].balance !== undefined) {
      titles[0].balance = Number(((titles[0].balance || 0) + difference).toFixed(2));
    }
    return titles;
  }

  /**
   * @deprecated
   */
  async assemblyTitlesFromInvoice(invoice: Invoice, finance: Finance, titleType: string, expenseType: string, os: MkgOS): Promise<CashTitle[]> {
    let titles = [];
    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(invoice.dispatchDate),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        supplier_clientId: invoice.sender,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType,
        parcel: parcel.seq,
        invoiceNumber: invoice.number,
        serial: invoice.serial,
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (os && os.codeSystem) {
        title.companyCode = os.codeSystem.toString();
        title.idOS = os.id;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
  * @deprecated
  */
  async assemblyTitlesFromNF(invoice: Pick<NF_response, "numero" | "serie">, finance: Finance, titleType: string, expenseType: string, clientId?: string): Promise<CashTitle[]> {
    let titles = [];

    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType || "",
        parcel: parcel.seq,
        invoiceNumber: invoice.numero.toString(),
        serial: invoice.serie.toString(),
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (clientId) {
        title['supplier_clientId'] = clientId;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
   * @deprecated
   */
  async assemblyTitlesFromNFSE(invoice: NFSe, finance: Finance, titleType: string, expenseType: string, clientId?: string): Promise<CashTitle[]> {
    let titles = [];

    for (const parcel of finance.paymentCondition.parcels) {
      const titleValue = Utilities.calcPercentage(parcel.percentage, finance.value);
      let title: CashTitle = {
        status: 1,
        value: titleValue,
        movementDate: new Date(),
        expirationDate: await firstValueFrom(this.getExpirationDate(parcel.days)),
        typeId: titleType,
        paymentConditionId: finance.paymentCondition._id,
        expenseTypeId: expenseType || "",
        parcel: parcel.seq,
        invoiceNumber: invoice.RPS[0].RPSNumero.toString(),
        serial: invoice.RPS[0].RPSSerie,
        balance: titleValue,
        adiantamento: false,
        rateTitle: 0
      }
      if (clientId) {
        title['supplier_clientId'] = clientId;
      }
      titles.push(title)
    }

    return this.fixDifference(titles, finance.value);
  }

  /**
   * @TODO Store the user selected finances into invoice
   * @param nfe The NFe, attention: NFCe hanven't tag <cobr>
   * @param args
   * @returns The title to create
   */
  assemblyTitlesFromDuplicates(nfe: NFe, args: {
    operation: Operation,
    supplier_clientId: ObjectId,
    paymentConditionId: ObjectId
  }) {
    const titles: CashTitle[] = [];

    for (const dup of nfe.cobr.dup) {
      titles.push({
        status: 1,
        value: dup.vDup,
        balance: dup.vDup,
        adiantamento: false,
        discount: 0,
        movementDate: new Date(nfe.ide.dhEmi),
        expirationDate: moment(dup.dVenc).endOf("day").toDate(), // avoiding fuso
        invoiceNumber: `${nfe.ide.nNF}`,
        serial: `${nfe.ide.serie}`,
        parcel: Number(dup.nDup),
        received: [],
        qtdaPortion: nfe.cobr.dup.length,
        typeId: args.operation.titleTypeId,
        expenseTypeId: args.operation.expenseTypeId,
        supplier_clientId: args.supplier_clientId,
        paymentConditionId: args.paymentConditionId
      })
    }

    return titles;
  }

  /**
   * @TODO Store the user selected finances into invoice
   * @returns The titles to create
   */
  assemblyTitlesFromRPS(rps: RPS, args: {
    operation: Operation,
    supplier_clientId: ObjectId,
    paymentConditionId: ObjectId
  }): CashTitle[] {
    const titles: CashTitle[] = [];

    for (let parcel of rps.ListaParcelas) {

      titles.push({
        status: 1,
        value: Number(parcel.PrcValor),
        balance: Number(parcel.PrcValor),
        adiantamento: false,
        discount: 0,
        movementDate: new Date(rps.dCompetencia),
        expirationDate: moment(parcel.PrcDtaVencimento).endOf("day").toDate(),
        invoiceNumber: `${rps.RPSNumero}`,
        serial: `${rps.RPSSerie}`,
        parcel: Number(parcel.PrcNroFatura),
        received: [],
        qtdaPortion: rps.ListaParcelas.length,
        typeId: args.operation.titleTypeId,
        expenseTypeId: args.operation.expenseTypeId,
        supplier_clientId: args.supplier_clientId,
        paymentConditionId: args.paymentConditionId
      })
    }

    return titles;
  }
  /**
   * Apply low (payment/receipt) to a title
   * 
   * @param titleId - ID of the title
   * @param receive - Receive object with payment details
   * @param cardAdminInfo - Card admin configuration (optional)
   * @param typeCalculateLow - Type of calculation (DISCOUNT, SURCHARGE, etc)
   * @returns Promise with the updated title and related data
   */
  /**
   * @param options.skipSourceProcessing the caller already applied the cashier/bank movement
   * and the retroactive adjustment; without it the server would post them a second time.
   */
  async applyLow(
    titleId: string,
    receive: Receive,
    cardAdminInfo?: any,
    typeCalculateLow?: ApplyLowOptions,
    cnpj?: string,
    options?: ApplyLowRequestOptions
  ): Promise<ApplyLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/apply-low`;
    const companyCnpj = cnpj || this.dataService.company.cnpj;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(companyCnpj)),
      options?.operationId
    );
    const mappedType = typeCalculateLow && ApplyLowOptions[typeCalculateLow] || "NONE";

    return firstValueFrom(
      this.http.post<ApplyLowResponse>(url, {
        receive,
        cardAdminInfo,
        typeCalculateLow: mappedType,
        ...(options?.skipSourceProcessing ? { skipSourceProcessing: true } : {}),
        ...(options?.correctTitleValue ? { correctTitleValue: true } : {}),
        ...(options?.reason ? { reason: options.reason } : {}),
        ...(options?.renegotiationTitleIds ? { renegotiationTitleIds: options.renegotiationTitleIds } : {})
      }, header)
    );
  }

  /**
   * Apply multiple lows
   * 
   * @param titles - Array of titles with their receive data
   * @param cnpj - Optional CNPJ to override the default company
   * @returns Promise with the results of all operations
   */
  async applyMultipleLows(
    titles: Array<{
      titleId: string;
      receive: Receive;
      cardAdminInfo?: any;
      typeCalculateLow?: ApplyLowOptions;
    }>,
    cnpj?: string,
    options?: { operationId?: string }
  ): Promise<MultipleApplyLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/apply-multiple-lows`;
    const companyCnpj = cnpj || this.dataService.company.cnpj;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(companyCnpj)),
      options?.operationId
    );

    // Map ApplyLowOptions enum values to string values for API
    const mappedTitles = titles.map(t => {
      const mappedType = t.typeCalculateLow && ApplyLowOptions[t.typeCalculateLow] || "NONE";
      return { ...t, typeCalculateLow: mappedType };
    });

    return firstValueFrom(
      this.http.post<MultipleApplyLowResponse>(url, { titles: mappedTitles }, header)
    );
  }

  async cancelLow(
    titleId: string,
    receiveId: string,
    options?: ICancelLowOptions,
    cnpj?: string
  ): Promise<CancelLowResponse> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/cancel-low`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = this._withOperationId(
      await firstValueFrom(this.dataService.httpOptions(companyId)),
      options?.operationId
    );
    const body = {
      receiveId,
      ...(options?.confirmCascade ? { confirmCascade: true } : {})
    };

    return firstValueFrom(
      this.http.post<CancelLowResponse>(url, body, header)
    );
  }

  private _withOperationId<T extends { headers: HttpHeaders }>(options: T, operationId?: string): T {
    if (!operationId) {
      return options;
    }
    return { ...options, headers: options.headers.set(OPERATION_ID_HEADER, operationId) };
  }

  /**
   * Update ONLY the balance of a single low (received) atomically.
   * Does not send the whole title, so it can never drop other receives. The
   * movement log (28 manual / 29 fix) is written server-side by TitleLowService.
   */
  async updateLowBalance(
    titleId: string,
    receiveId: string,
    balance: number,
    reason: 'fix' | 'manual',
    cnpj?: string
  ): Promise<{ success: boolean; title: CashTitle }> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/received/${receiveId}/balance`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyId));

    return firstValueFrom(
      this.http.patch<{ success: boolean; title: CashTitle }>(url, { balance, reason }, header)
    );
  }

  /**
   * Partially update title fields. Only the fields sent are touched, so there is no need to
   * fetch and resend the whole title. Server-side allowlist; `received` is rejected — use
   * `patchReceivedBatch`, `applyLow` or `cancelLow` for lows.
   */
  async patchTitle(
    titleId: string,
    fields: TitlePatchFields,
    cnpj?: string
  ): Promise<CashTitle> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyId));

    return firstValueFrom(this.http.patch<CashTitle>(url, fields, header));
  }

  /**
   * Detaches a title from the OS that generated it — `companyCode`/`idOS` cleared together
   * (never reassigned, per the PATCH origin-pair rule). Used when reopening an OS whose title
   * was already canceled without an INACTIVE substitute: the canceled title stops counting as
   * "this OS already has a title" anywhere that looks it up by `idOS`/`companyCode`, while
   * staying intact for financial history. `observation` is user-owned free text and is never
   * touched here — the detach itself is auditable through the title's change log (server-side
   * `PATCH /title/:id` records the `idOS`/`companyCode` before/after).
   */
  async unlinkOsReference(titleId: string, cnpj?: string): Promise<CashTitle> {
    return this.patchTitle(titleId, { companyCode: null, idOS: null }, cnpj);
  }

  /**
   * Update fields of SEVERAL lows of the same title in one request, each identified by `_id`.
   * Lows not mentioned stay untouched. The movement log is written server-side; pass
   * `movementType` to keep the history entry a migrated PUT-based flow used to produce.
   */
  async patchReceivedBatch(
    titleId: string,
    lows: Array<{ receiveId: string; balance?: number; description?: string }>,
    options?: { reason?: 'fix' | 'manual'; movementType?: CASH_TITLE_MOVEMENT_TYPE },
    cnpj?: string
  ): Promise<{ success: boolean; title: CashTitle }> {
    const url = `${environment.mkgoURL}/api/v1/title/${titleId}/received`;
    const companyId = cnpj || this.dataService.company.cnpj;
    const header = await firstValueFrom(this.dataService.httpOptions(companyId));
    const body = {
      received: lows,
      reason: options?.reason ?? 'manual',
      ...(options?.movementType !== undefined ? { movementType: options.movementType } : {})
    };

    return firstValueFrom(
      this.http.patch<{ success: boolean; title: CashTitle }>(url, body, header)
    );
  }
}

export interface ApplyLowResponse {
  success: boolean;
  title: CashTitle;
  cashier?: any;
  bank?: any;
  performanceLog?: any;
}

export interface MultipleApplyLowResponse {
  success: boolean;
  processed: number;
  errors: number;
  results: Array<{
    titleId: string;
    success: boolean;
    title: CashTitle;
  }>;
  errorsList: Array<{
    titleId: string;
    error: string;
  }>;
}

export interface CancelLowResponse {
  success: boolean;
  title: CashTitle;
  cashier?: any;
  bank?: any;
  retroactive?: any;
  performanceLog?: any;
}
